/**
 * Deep-copies a plain-data default so each instance owns its own copy.
 * Uses `structuredClone` when available; falls back to a recursive clone that
 * preserves `undefined`-valued keys (which `JSON` round-tripping would drop).
 * For exotic types (Date, Map, class instances) pass a factory instead.
 */
function cloneDefault<T>(value: T): T {
  if (typeof structuredClone === 'function') return structuredClone(value);
  if (Array.isArray(value)) return value.map(cloneDefault) as T;
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, cloneDefault(val)])) as T;
  }
  return value;
}

/**
 * Property decorator for `@Input()`s that need a default value.
 *
 * Field initializers (`@Input() pageSize = 10`) only run at construction time.
 * When a binding resolves to `undefined` (e.g. `[pageSize]="undefined"`),
 * Angular assigns `undefined` and wipes the initializer. This installs a
 * get/set accessor that coalesces `undefined` back to the default.
 *
 * Mutable defaults are copied per instance so components don't share state
 * (e.g. `sortBy` is mutated in place by the table). Pass a factory for full
 * control over how the default is produced.
 *
 * @example
 * \@Input() \@InputDefault(10) pageSize: number;
 * \@Input() \@InputDefault({ key: undefined, direction: undefined }) sortBy: TableSortInterface<T>;
 * \@Input() \@InputDefault(() => new Date()) since: Date;
 */
export function InputDefault<T>(defaultValueOrFactory: T | (() => T)): PropertyDecorator {
  const makeDefault = (): T => {
    if (typeof defaultValueOrFactory === 'function') {
      return (defaultValueOrFactory as () => T)();
    }
    if (defaultValueOrFactory !== null && typeof defaultValueOrFactory === 'object') {
      return cloneDefault(defaultValueOrFactory);
    }
    return defaultValueOrFactory;
  };

  return (target: object, propertyKey: string | symbol): void => {
    const valueKey = Symbol(`__inputDefault_${String(propertyKey)}`);
    const setKey = Symbol(`__inputDefault_${String(propertyKey)}_isSet`);

    Object.defineProperty(target, propertyKey, {
      get(this: Record<symbol, unknown>): T {
        if (this[setKey]) return this[valueKey] as T;

        // If the setter has never been called, set default value
        // Should only happen during initialisation
        this[valueKey] = makeDefault();
        this[setKey] = true;
        return this[valueKey] as T;
      },
      set(this: Record<symbol, unknown>, value: T | undefined): void {
        this[valueKey] = value === undefined ? makeDefault() : value;
        this[setKey] = true;
      },
      enumerable: true,
      configurable: true,
    });
  };
}
